import { useState } from "react";
import { toast } from "sonner";
import { FormPage, TextField, AreaField, SelectField, SubmitButton, SuccessPanel } from "@/components/forms";
import { api, apiError } from "@/lib/api";

const EMPTY = {
  your_name: "",
  relationship: "",
  email: "",
  phone: "",
  current_setting: "",
  care_needs: "",
  timeframe: "",
};

export default function RequestCare() {
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  async function onSubmit(e) {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/forms/request-care", form);
      setSent(true);
      toast.success("Request received — we'll be in touch.");
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setLoading(false);
    }
  }

  return (
    <FormPage
      testid="request-care-page"
      eyebrow="Request Care"
      lines={["Tell us what", "home should feel like."]}
      intro="A few details are all we need to start the conversation. There's no commitment — this simply opens the door."
      asideTitle="Prefer to talk it through?"
      asideText="You can also schedule a consultation if you'd rather speak with someone before sharing details."
    >
      {sent ? (
        <SuccessPanel
          testid="request-care-success"
          title="Request received"
          message="Thank you for trusting us with this. A member of the Homevia team will follow up with you to talk through next steps."
          onReset={() => {
            setForm(EMPTY);
            setSent(false);
          }}
        />
      ) : (
        <form onSubmit={onSubmit} data-testid="request-care-form" className="space-y-6">
          <div className="grid gap-6 sm:grid-cols-2">
            <TextField id="rc-name" label="Your name" required value={form.your_name} onChange={set("your_name")} />
            <SelectField
              id="rc-relationship"
              label="Your relationship to the patient"
              required
              value={form.relationship}
              onChange={set("relationship")}
              options={["Myself", "Spouse or partner", "Parent", "Child", "Sibling", "Other family", "Friend or caregiver"]}
            />
          </div>
          <div className="grid gap-6 sm:grid-cols-2">
            <TextField id="rc-email" label="Email" type="email" required value={form.email} onChange={set("email")} />
            <TextField id="rc-phone" label="Phone" type="tel" required value={form.phone} onChange={set("phone")} />
          </div>
          <div className="grid gap-6 sm:grid-cols-2">
            <SelectField
              id="rc-setting"
              label="Where is the patient now?"
              value={form.current_setting}
              onChange={set("current_setting")}
              options={["Hospital", "Rehabilitation facility", "Skilled nursing facility", "Already home", "Other"]}
            />
            <SelectField
              id="rc-timeframe"
              label="When do you need support?"
              value={form.timeframe}
              onChange={set("timeframe")}
              options={["As soon as possible", "Within the next few weeks", "Planning ahead", "Not sure yet"]}
            />
          </div>
          <AreaField
            id="rc-needs"
            label="What kind of support are you looking for?"
            required
            value={form.care_needs}
            onChange={set("care_needs")}
            placeholder="Share anything that would help us understand the situation — no medical detail required."
          />
          <SubmitButton loading={loading} testid="request-care-submit-btn">
            Submit Request
          </SubmitButton>
        </form>
      )}
    </FormPage>
  );
}
